/**
 * Saved-search disposition display groups — mirrors backend
 * saved_search_tender_relevance.py (DISPLAY_GROUP_*, group_saved_search_new_matches).
 */

/** @typedef {'opportunities'|'contract_awards'|'grants'|'other'} SavedSearchDisplayGroup */

/** @type {readonly SavedSearchDisplayGroup[]} */
export const DISPLAY_GROUP_ORDER = Object.freeze([
  'opportunities',
  'contract_awards',
  'grants',
  'other',
]);

/** @type {Readonly<Record<SavedSearchDisplayGroup, string>>} */
export const DISPLAY_GROUP_TITLES = Object.freeze({
  opportunities: 'Open opportunities matching your saved searches',
  contract_awards: 'New contract awards matching your saved searches',
  grants: 'New grants matching your saved searches',
  other: 'Other matches from your saved searches',
});

/** Header filter labels (shorter than subsection titles). */
/** @type {Readonly<Record<SavedSearchDisplayGroup, string>>} */
export const NOTICE_TYPE_FILTER_LABELS = Object.freeze({
  opportunities: 'Open opportunities',
  contract_awards: 'Contract awards',
  grants: 'Grants',
  other: 'Other',
});

/** @type {ReadonlyArray<{ value: string, text: string }>} */
export const NOTICE_TYPE_FILTER_OPTIONS = Object.freeze([
  { value: 'all', text: 'All notice types' },
  ...DISPLAY_GROUP_ORDER.map((key) => ({
    value: key,
    text: NOTICE_TYPE_FILTER_LABELS[key],
  })),
]);

const KIND_TO_DISPLAY_GROUP = Object.freeze({
  request_tender: 'opportunities',
  grant_opportunity: 'opportunities',
  contract_notice: 'contract_awards',
  grant: 'grants',
  other: 'other',
});

/**
 * @param {string} disposition
 * @returns {'request_tender'|'contract_notice'|'grant_opportunity'|'grant'|'other'}
 */
export function dispositionKind(disposition) {
  const normalized = (disposition || '').trim().toLowerCase();
  if (normalized === 'request' || normalized === 'tender' || normalized === 'request / tender') {
    return 'request_tender';
  }
  if (normalized === 'contract notice') {
    return 'contract_notice';
  }
  if (normalized === 'grant opportunity') {
    return 'grant_opportunity';
  }
  if (normalized === 'grant') {
    return 'grant';
  }
  return 'other';
}

/**
 * Map a disposition label to a digest display bucket.
 *
 * @param {string|null|undefined} disposition
 * @returns {SavedSearchDisplayGroup}
 */
export function dispositionDisplayGroup(disposition) {
  const kind = dispositionKind(disposition);
  return KIND_TO_DISPLAY_GROUP[kind] || 'other';
}

/**
 * @param {*} value
 * @returns {Date|null}
 */
export function parseDisplayDate(value) {
  if (value == null) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  const s = String(value).trim();
  if (!s) return null;
  const normalized = s.endsWith('Z') ? s.slice(0, -1) : s.split('+')[0];
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * @param {Object} row
 * @returns {number}
 */
function publishedDateSortKey(row) {
  const parsed = parseDisplayDate(row.published_date);
  return parsed ? parsed.getTime() : 0;
}

/**
 * @param {Object} row
 * @returns {number}
 */
function displayDateSortKey(row) {
  const parsed = parseDisplayDate(row.display_date);
  return parsed ? parsed.getTime() : 0;
}

/**
 * @param {Object} row
 * @returns {number}
 */
function contractStartSortKey(row) {
  const parsed = parseDisplayDate(row.contract_start || row.display_date);
  return parsed ? parsed.getTime() : 0;
}

/**
 * @param {SavedSearchDisplayGroup} group
 * @param {Object} row
 * @returns {number}
 */
function sortKeyForGroup(group, row) {
  if (group === 'opportunities') return publishedDateSortKey(row);
  if (group === 'contract_awards') return contractStartSortKey(row);
  return displayDateSortKey(row);
}

/**
 * Split digest rows into disposition display groups.
 * Opportunities: published_date desc. Contract awards: contract_start desc. Other: display_date desc.
 *
 * @param {Array<Object>} rows
 * @returns {Record<SavedSearchDisplayGroup, Array<Object>>}
 */
export function groupSavedSearchNewMatches(rows) {
  /** @type {Record<SavedSearchDisplayGroup, Array<Object>>} */
  const buckets = {
    opportunities: [],
    contract_awards: [],
    grants: [],
    other: [],
  };

  if (!Array.isArray(rows) || rows.length === 0) {
    return buckets;
  }

  for (const row of rows) {
    const group = dispositionDisplayGroup(row.disposition);
    buckets[group].push(row);
  }

  for (const group of DISPLAY_GROUP_ORDER) {
    buckets[group].sort((a, b) => sortKeyForGroup(group, b) - sortKeyForGroup(group, a));
  }

  return buckets;
}

/**
 * Flat list in DISPLAY_GROUP_ORDER (for section pagination).
 *
 * @param {Array<Object>} rows
 * @returns {Array<Object>}
 */
export function flattenSavedSearchNewMatchesInGroupOrder(rows) {
  const groups = groupSavedSearchNewMatches(rows);
  return DISPLAY_GROUP_ORDER.flatMap((key) => groups[key] || []);
}

/**
 * Non-empty groups in order, for subsection rendering.
 *
 * @param {Array<Object>} rows
 * @returns {Array<{ key: SavedSearchDisplayGroup, title: string, rows: Array<Object> }>}
 */
export function savedSearchDisplayGroupSections(rows) {
  const groups = groupSavedSearchNewMatches(rows);
  return DISPLAY_GROUP_ORDER.filter((key) => (groups[key] || []).length > 0).map((key) => ({
    key,
    title: DISPLAY_GROUP_TITLES[key],
    rows: groups[key],
  }));
}

/**
 * Whether a row should use the opportunity card layout (vs award).
 *
 * @param {SavedSearchDisplayGroup} groupKey
 * @returns {boolean}
 */
export function isOpportunityDisplayGroup(groupKey) {
  return groupKey === 'opportunities';
}
